import {
  createRootRoute,
  Outlet,
  redirect,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { Spinner } from '@osd/ui/spinner';
import { AppShell } from '../components/AppShell/AppShell';
import { useAuth } from '../features/auth/AuthProvider';

/**
 * OSD is members-only (root/osd#22, root/osd#27).
 *
 * Public routes:
 *   /login              — sign in
 *   /register?invite=…  — invited registration (the page renders the
 *                          "invite required" message when the token
 *                          is missing — the URL itself is the
 *                          pre-invitation landing surface)
 *
 * Everything else requires an authenticated session. The `beforeLoad`
 * guard below redirects unauthenticated visitors to /login, preserving
 * the originally-requested URL in `next` so post-login lands the user
 * back where they started.
 *
 * API routes (/api/*) are served by the Express API, not the SPA
 * router — they bypass this guard entirely. The `/health` path is
 * the SPA's index.html fallback (nginx); the JSON health probe lives
 * at `/api/health` (root/osd#54).
 */
const PUBLIC_PATHS: ReadonlySet<string> = new Set(['/login', '/register']);

function isPublic(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname);
}

/**
 * Build the `next` value to stash on /login. We deliberately strip
 * any existing `next=` from the search string before composing it
 * — otherwise the redirect history gets baked back in and the URL
 * nests on every redirect (`?next=%2Flogin%3Fnext%3D%2Flogin…`).
 *
 * We also refuse to point at /login itself (or any other public
 * path) — bouncing a logged-out user between /login and /login
 * is the classic loop this helper is meant to prevent.
 */
function buildNext(
  pathname: string,
  searchStr: string | undefined,
  hash: string | undefined,
): string {
  if (isPublic(pathname)) return '/';
  const params = new URLSearchParams(searchStr ?? '');
  params.delete('next');
  const cleanedSearch = params.toString();
  return pathname + (cleanedSearch ? `?${cleanedSearch}` : '') + (hash ? `#${hash}` : '');
}

/**
 * Validate the `next` URL the user is trying to return to. We
 * accept any same-origin absolute path (`/users`,
 * `/components/getting-started`, …). We reject:
 *  - cross-origin URLs (don't follow external links on sign-in)
 *  - protocol-relative URLs (`//evil.example/x`)
 *  - anything that doesn't start with `/`
 *  - the public routes themselves (would re-enter the login
 *    bounce — `/login?next=/login` is the classic loop)
 *  - paths with embedded `?next=` or `#` already — indicates the
 *    user has been bouncing and we should land them somewhere
 *    sensible instead of compounding the nesting
 */
function safeNext(raw: string | undefined): string {
  if (raw === undefined || raw === '') return '/';
  // Must start with a single `/` (not `//`).
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  // Strip query + hash so we can validate just the path.
  let parsedPath: string;
  try {
    const u = new URL(raw, 'https://placeholder.invalid');
    parsedPath = u.pathname;
  } catch {
    return '/';
  }
  if (isPublic(parsedPath)) return '/';
  // Already-redirected markers in the original URL — refuse to
  // bounce again.
  if (/(?:^|[?&])next=/.test(raw)) return '/';
  return raw;
}

export const Route = createRootRoute({
  component: RootLayout,
  beforeLoad: ({ location }) => {
    // Legacy /components → /ui redirect. The design-system docs
    // surface moved from /components to /ui in root/osd#81 (UI Kit
    // + Icon Kit split). Old links keep working without a 404.
    // Matches `/components`, `/components/<slug>`, and trailing slash.
    if (location.pathname === '/components' || location.pathname === '/components/') {
      throw redirect({ to: '/ui' });
    }
    if (location.pathname.startsWith('/components/')) {
      throw redirect({
        to: '/ui' + location.pathname.slice('/components'.length),
      });
    }
    if (isPublic(location.pathname)) return;
    if (typeof window === 'undefined') return; // SSR — skip
    const hasToken = localStorage.getItem('osd.auth.access') !== null;
    if (!hasToken) {
      // Preserve the originally-requested URL in `next` so post-
      // login lands the user back where they started. Any
      // pre-existing `next=` is stripped first — see buildNext().
      throw redirect({
        to: '/login',
        search: {
          next: buildNext(location.pathname, location.searchStr, location.hash),
        },
      });
    }
  },
});

function RootLayout() {
  // Read the pathname from TanStack Router's reactive location,
  // not from window.location — TanStack Router subscribes to its
  // own navigation state and re-renders consumers on every
  // navigate(); reading window.location.pathname directly bypasses
  // that subscription and can desync (e.g. after login navigates
  // to '/', the old code path briefly saw the new URL and
  // unwrapped ProtectedRoute while auth.user was still in the
  // "just-set" tick — AppShell saw null, returned null, no shell).
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.has(location.pathname);

  if (isPublic) {
    return <Outlet />;
  }

  // ProtectedRoute — render-time guard that watches the auth
  // signal. Catches the cases where `beforeLoad` (token
  // presence) was satisfied but the auth probe or a later 401
  // has flipped `user` back to null (e.g. token expired mid-
  // session). AppShell already returns null when user is null;
  // this effect drives the URL change to /login.
  return (
    <ProtectedRoute>
      <AppShell>
        <Outlet />
      </AppShell>
    </ProtectedRoute>
  );
}

/**
 * ProtectedRoute — wraps a protected subtree. While the user is
 * authenticated, renders children. When the user becomes null
 * (logout, token-expired 401, failed /me probe), navigates to
 * /login preserving the current path via `?next=…`.
 *
 * The AppShell inside the children also self-guards (returns
 * null when user is null) so no chrome leaks during the brief
 * gap between "user flipped to null" and "navigation completes".
 */
function ProtectedRoute({ children }: { readonly children: React.ReactNode }): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only fire the redirect when the auth probe has DEFINITIVELY
    // concluded that there is no user. While the probe is still in
    // flight (`loading === true`), `user` is temporarily null but we
    // have a token in localStorage (the probe hasn't returned yet).
    // Firing the redirect during that window would push a valid
    // session to /login as soon as they hit any protected route —
    // a guaranteed infinite redirect loop.
    //
    // We also bail out on the public routes themselves — the router
    // should never mount this effect there, but defensive: an
    // out-of-order effect could otherwise send /login → /login.
    if (auth.loading) return;
    if (auth.user !== null) return;
    void navigate({
      to: '/login',
      search: {
        next: buildNext(location.pathname, location.searchStr, location.hash),
      },
      replace: true,
    });
  }, [auth.loading, auth.user, navigate, location.pathname, location.searchStr, location.hash]);

  // While the auth probe is in flight (loading=true, user=null)
  // AND we're on a protected route, render nothing. Once the
  // probe completes, user is either set (we render children)
  // or null (the effect above fires the redirect).
  if (auth.user === null) {
    // While loading, render a thin centered placeholder so the
    // page doesn't feel broken. After loading, the effect will
    // navigate away; if for any reason it doesn't (e.g. race),
    // we render null so nothing of the app leaks.
    if (auth.loading) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--osd-color-fg-muted)',
          }}
          aria-busy="true"
        >
          <Spinner size="lg" ariaLabel="Verifying session" />
        </div>
      );
    }
    return <></>;
  }
  return <>{children}</>;
}
